import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, Crown, Gift, Search, RefreshCw, 
  Check, X, Clock, AlertTriangle, Shield,
  Calendar, Mail, Building
} from 'lucide-react';

const AdminSubscriptionsPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Gift dialog state
  const [giftDialogOpen, setGiftDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [giftMonths, setGiftMonths] = useState('1');
  const [gifting, setGifting] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (user && user.role !== 'admin') {
      toast.error('Alleen admins hebben toegang tot deze pagina');
      navigate('/company/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Fout bij ophalen gebruikers');
    } finally {
      setLoading(false);
    }
  };

  const handleGiftSubscription = async () => {
    if (!selectedUser) return;
    
    setGifting(true);
    try {
      await api.post('/admin/subscriptions/gift', {
        user_id: selectedUser.id,
        months: parseInt(giftMonths)
      });
      toast.success(`Subscription van ${giftMonths} maanden gegeven aan ${selectedUser.name}`);
      setGiftDialogOpen(false);
      setSelectedUser(null);
      setGiftMonths('1');
      fetchUsers(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij geven subscription');
    } finally {
      setGifting(false);
    }
  };

  const openGiftDialog = (user) => {
    setSelectedUser(user);
    setGiftDialogOpen(true);
  };

  const getStatusBadge = (subscription) => {
    if (!subscription) return <Badge variant="outline">Onbekend</Badge>;
    
    const status = subscription.status;
    switch (status) {
      case 'admin':
        return <Badge className="bg-purple-600"><Shield className="w-3 h-3 mr-1" />Admin</Badge>;
      case 'active':
        return <Badge className="bg-green-600"><Check className="w-3 h-3 mr-1" />Actief</Badge>;
      case 'trial':
        return <Badge className="bg-blue-600"><Clock className="w-3 h-3 mr-1" />Trial</Badge>;
      case 'gifted':
        return <Badge className="bg-yellow-600"><Gift className="w-3 h-3 mr-1" />Cadeau</Badge>;
      case 'cancelled':
        return <Badge className="bg-orange-600"><AlertTriangle className="w-3 h-3 mr-1" />Opgezegd</Badge>;
      case 'expired':
        return <Badge className="bg-red-600"><X className="w-3 h-3 mr-1" />Verlopen</Badge>;
      case 'none':
        return <Badge variant="outline">Geen</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getEndDate = (subscription) => {
    if (!subscription) return '-';
    if (subscription.status === 'admin') return 'N.v.t.';
    if (subscription.status === 'trial') return formatDate(subscription.trial_end);
    return formatDate(subscription.subscription_end);
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filterStatus === 'all' || 
      u.subscription?.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Stats
  const stats = {
    total: users.length,
    admin: users.filter(u => u.subscription?.status === 'admin').length,
    active: users.filter(u => u.subscription?.status === 'active').length,
    trial: users.filter(u => u.subscription?.status === 'trial').length,
    gifted: users.filter(u => u.subscription?.status === 'gifted').length,
    expired: users.filter(u => ['expired', 'none', 'cancelled'].includes(u.subscription?.status)).length,
  };

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-subscriptions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Crown className="w-7 h-7 text-orange-600" />
            Subscription Beheer
          </h1>
          <p className="text-gray-600 mt-1">Beheer gebruikers en hun subscriptions</p>
        </div>
        <Button onClick={fetchUsers} variant="outline" data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Vernieuwen
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Users className="w-4 h-4" /> Totaal
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-purple-600">{stats.admin}</div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Shield className="w-4 h-4" /> Admins
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Check className="w-4 h-4" /> Actief
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{stats.trial}</div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Clock className="w-4 h-4" /> Trial
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.gifted}</div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Gift className="w-4 h-4" /> Cadeau
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-red-600">{stats.expired}</div>
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <X className="w-4 h-4" /> Verlopen
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Zoek op naam of email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-select">
                <SelectValue placeholder="Filter op status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statussen</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="active">Actief</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="gifted">Cadeau</SelectItem>
                <SelectItem value="cancelled">Opgezegd</SelectItem>
                <SelectItem value="expired">Verlopen</SelectItem>
                <SelectItem value="none">Geen</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Gebruikers ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-500 mt-2">Laden...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto text-gray-300" />
              <p className="text-gray-500 mt-2">Geen gebruikers gevonden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Gebruiker</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Einddatum</TableHead>
                    <TableHead>Aangemaakt</TableHead>
                    <TableHead className="text-right">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <span className="text-orange-600 font-medium">
                              {u.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium">{u.name || 'Onbekend'}</div>
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {u.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Building className="w-3 h-3" />
                          {u.role}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(u.subscription)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="w-3 h-3" />
                          {getEndDate(u.subscription)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDate(u.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        {u.role !== 'admin' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openGiftDialog(u)}
                            data-testid={`gift-btn-${u.id}`}
                          >
                            <Gift className="w-4 h-4 mr-1" />
                            Cadeau
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gift Subscription Dialog */}
      <Dialog open={giftDialogOpen} onOpenChange={setGiftDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-yellow-600" />
              Subscription Cadeau Geven
            </DialogTitle>
            <DialogDescription>
              Geef een gratis subscription aan {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="font-medium">{selectedUser?.name}</div>
              <div className="text-sm text-gray-500">{selectedUser?.email}</div>
              <div className="text-sm text-gray-500 mt-1">
                Huidige status: {selectedUser?.subscription?.status || 'geen'}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Aantal maanden</Label>
              <Select value={giftMonths} onValueChange={setGiftMonths}>
                <SelectTrigger data-testid="gift-months-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 maand</SelectItem>
                  <SelectItem value="3">3 maanden</SelectItem>
                  <SelectItem value="6">6 maanden</SelectItem>
                  <SelectItem value="12">12 maanden</SelectItem>
                  <SelectItem value="24">24 maanden</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setGiftDialogOpen(false)}
              disabled={gifting}
            >
              Annuleren
            </Button>
            <Button 
              onClick={handleGiftSubscription}
              disabled={gifting}
              className="bg-yellow-600 hover:bg-yellow-700"
              data-testid="confirm-gift-btn"
            >
              {gifting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Gift className="w-4 h-4 mr-2" />
              )}
              {giftMonths} maand{giftMonths !== '1' ? 'en' : ''} geven
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubscriptionsPage;
